import { useNavigate } from 'react-router-dom';
import { ExternalLink, Terminal, Cpu, Network, Archive } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button, LinkCard } from '@vnetwork/ui/primitives';
import { SplitView, Section } from '@vnetwork/ui/layout';
import { formatDate } from '@/lib/format-date';
import type { Instance } from '@/api/opennebula';
import { copyToClipboard, instanceIps } from '../shared';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';
import { ApplicationHealthLogs } from './ApplicationHealthLogs';

function isApplication(instance: Instance): boolean {
  return instance.metadata?.application?.metadata?.type === 'application';
}

function applicationUrl(instance: Instance): string | undefined {
  const app = instance.metadata?.application;
  const ip = instance.metadata?.nics?.[0]?.ip;
  if (!app?.healthz || !ip) return undefined;
  const host = app.metadata?.fields?.find((f) => f.key === 'N8N_HOSTNAME')?.value;
  return app.healthz.replace('PUBLIC_IP', host || ip);
}

export function OverviewTab({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const navigate = useNavigate();
  const m = instance.metadata;
  const isApp = isApplication(instance);
  const appUrl = applicationUrl(instance);
  const poweredOn = instance.status === 'POWERED_ON';
  const r = m?.resources;
  const { publicIp, privateIp, floatingIp, dns } = instanceIps(m);
  const secgroupCount = (m?.nics ?? []).reduce((n, nic) => n + (nic.security_groups?.length ?? 0), 0);

  const base = `/services/compute/${serviceId}/instances/${instance.id}`;
  const openConsole = () => window.open(`${base}/console`, instance.id, 'height=840,width=1250');
  // SPA navigation for the quick-action LinkCards (they render as <a> for a11y).
  const go = (path: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(path); };

  const primaryAction = isApp ? (
    <Button size="sm" startIcon={<ExternalLink size={15} />} disabled={!poweredOn || !appUrl}
      onClick={() => appUrl && window.open(appUrl, '_blank', 'noopener,noreferrer')}>
      Open application
    </Button>
  ) : (
    <Button size="sm" variant="secondary" startIcon={<Terminal size={15} />} disabled={!poweredOn} onClick={openConsole}>
      Open console
    </Button>
  );

  // Rail: connection details + quick actions to the other tabs.
  const rail = (
    <>
      <Card flat>
        <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Connection</CardTitle></CardHeader>
        <CardBody>
          <DescList minColumn={999} style={{ gap: 'var(--vn-space-3)' }}>
            <DescItem label="Public IP">{publicIp ? <CopyableCode value={publicIp} /> : '—'}</DescItem>
            <DescItem label="Private IP">{privateIp ? <CopyableCode value={privateIp} /> : '—'}</DescItem>
            {floatingIp ? <DescItem label="Floating IP"><CopyableCode value={floatingIp} /></DescItem> : null}
            <DescItem label="DNS">{dns || '—'}</DescItem>
          </DescList>
        </CardBody>
      </Card>

      <Section title="Quick actions">
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <LinkCard icon={<Terminal size={16} />} title="Console" description="Open a VNC session" href={`${base}/console`} onClick={(e) => { e.preventDefault(); openConsole(); }} />
          <LinkCard icon={<Cpu size={16} />} title="Resize" description="Change vCPU & memory" href={`${base}/resize`} onClick={go(`${base}/resize`)} />
          <LinkCard icon={<Network size={16} />} title="Networking" description="NICs & security groups" href={`${base}/network`} onClick={go(`${base}/network`)} />
          <LinkCard icon={<Archive size={16} />} title="Backups" description="On-demand & scheduled" href={`${base}/backups`} onClick={go(`${base}/backups`)} />
        </div>
      </Section>
    </>
  );

  return (
    <SplitView asideWidth="300px" aside={rail}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Resource specs at a glance (Status lives in the page header pill). */}
      <Card flat>
        <CardBody>
          <CompactStatGroup columns={3}>
            <CompactStat label="vCPU" value={r?.cores ?? '—'} />
            <CompactStat label="Memory" value={r?.memory != null ? `${r.memory} GB` : '—'} />
            <CompactStat label="Disk" value={r?.volumes != null ? `${r.volumes} GB` : '—'} />
          </CompactStatGroup>
        </CardBody>
      </Card>

      {/* Identity — what the instance is, separate from how you reach it (rail). */}
      <Card>
        <CardHeader actions={primaryAction}><CardTitle>Instance information</CardTitle></CardHeader>
        <CardBody>
          <DescList>
            <DescItem label="Name">{instance.name || '—'}</DescItem>
            <DescItem label="Operating system">{m?.image?.name || '—'}</DescItem>
            <DescItem label="Flavor">{m?.flavor || '—'}</DescItem>
            <DescItem label="Security groups">{secgroupCount}</DescItem>
            <DescItem label="Zone">{instance.zone || '—'}</DescItem>
            <DescItem label="Instance ID"><code style={monoValue}>{instance.id}</code></DescItem>
            <DescItem label="Created">{instance.created_at ? formatDate(instance.created_at) : '—'}</DescItem>
            <DescItem label="Last modified">{instance.updated_at ? formatDate(instance.updated_at) : '—'}</DescItem>
          </DescList>
        </CardBody>
      </Card>

      {isApp && instance.metadata?.application && (
        <Card>
          <CardHeader><CardTitle>Application configuration</CardTitle></CardHeader>
          <CardBody>
            <DescList>
              <DescItem label="Application">{instance.metadata.application.name || '—'}</DescItem>
              <DescItem label="Distribution">{instance.metadata.application.distro || '—'}</DescItem>
              <DescItem label="Application URL">
                {appUrl ? (
                  <a href={appUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                    {appUrl} <ExternalLink size={12} />
                  </a>
                ) : '—'}
              </DescItem>
              <DescItem label="Template">{instance.metadata.application.template?.join(', ') || '—'}</DescItem>
            </DescList>

            {instance.metadata.application.metadata?.fields?.length ? (
              <>
                <div style={{ marginTop: 'var(--vn-space-4)', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>Environment</div>
                <DescList style={{ marginTop: 'var(--vn-space-2)' }}>
                  {instance.metadata.application.metadata.fields.map((f) => (
                    <DescItem key={f.key} label={f.key}>
                      {f.type === 'input' && f.key.toLowerCase().includes('pass') ? '••••••••' : f.value || '—'}
                    </DescItem>
                  ))}
                </DescList>
              </>
            ) : null}
          </CardBody>
        </Card>
      )}

      {isApp && <ApplicationHealthLogs serviceId={serviceId} instance={instance} />}
      </div>
    </SplitView>
  );
}

/** Monospace value that copies to clipboard on click (for IPs). */
function CopyableCode({ value }: { value: string }) {
  return (
    <code
      title="Click to copy"
      onClick={() => void copyToClipboard(value)}
      style={{ fontSize: 'var(--vn-font-size-sm)', cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </code>
  );
}

const linkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  color: 'var(--vn-accent-text, var(--vn-accent-solid))',
  textDecoration: 'none',
  wordBreak: 'break-all',
};
